import { useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';

import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Tabs,
  Tab,
} from '@nextui-org/react';
import { useState, useMemo } from 'react';
import { GroupedSettlement } from '../../../services/comparative-clientes/comparative-clients.utility';
import { formatMoneyUSD } from '../../../utils/functions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { QuartileDistribution } from '../../../services/comparative-industry/comparative-industry.utility';

type SortColumn = 'key' | 'mainKilos' | 'othersKilos' | 'perKiloDifference' | 'totalDifference' | 'pctVsMedian' | 'quartile' | 'q1' | 'q2' | 'q3' | 'q4';

interface Props {
  data: {
    byShips: { [key: string]: GroupedSettlement };
    byWeeks: { [key: string]: GroupedSettlement };
  };
  comparisonType: 'byWeeks' | 'byShips';
  currencyLabel?: string;
  pctVsMedianByKey?: Record<string, number>;
  quartileDistByKey?: Record<string, QuartileDistribution>;
  comparisonTarget?: 'median' | 'q1' | 'q3';
}

const CaliberComparisionBarChart: React.FC<Props> = ({
  data,
  comparisonType,
  currencyLabel,
  pctVsMedianByKey,
  quartileDistByKey,
  comparisonTarget = 'median',
}) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  const [displayMode, setDisplayMode] = useState<'value' | 'percentage'>(
    'value'
  );
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const chartRef = useRef<HTMLDivElement>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('totalDifference');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isLocal = currencyLabel && currencyLabel !== 'USD';

  const fmt = (value: number, decimals = 2) =>
    isLocal
      ? `${value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${currencyLabel}`
      : formatMoneyUSD(value, { decimals });

  const baseChartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 350,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          colors: {
            ranges: [
              { from: -Infinity, to: 0, color: '#f87171' },
              { from: 0, to: Infinity, color: '#4ade80' },
            ],
          },
        },
      },
      grid: {
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            strokeDashArray: 0,
            borderColor: '#FF0000',
            borderWidth: 2,
            opacity: 0.8,
            width: '100%',
            label: {
              borderColor: '#FF0000',
              style: { color: '#fff', background: '#FF0000' },
              text: '0',
            },
          },
        ],
      },
    }),
    []
  );

  const caliberOrder = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];

  const computedData = useMemo(() => {
    const allShipments = Object.values(data[comparisonType]);
    const caliberData: Record<
      string,
      { difference: number; fob: number; kilos: number; othersFob: number; othersKilos: number }
    > = {};

    allShipments.forEach((shipment) => {
      Object.entries(shipment.mainClient.details).forEach(
        ([detailKey, mainDetail]) => {
          const [, caliber] = detailKey.split('-');

          const mainFobPerKg = mainDetail.fob / mainDetail.kilos;

          let othersTotalKilos = 0;
          let othersTotalFob = 0;

          Object.values(shipment.comparisons).forEach((client) => {
            if (client.details[detailKey]) {
              othersTotalKilos += client.details[detailKey].kilos;
              othersTotalFob += client.details[detailKey].fob;
            }
          });

          if (mainDetail.kilos > 0 && othersTotalKilos > 0) {
            if (!caliberData[caliber]) {
              caliberData[caliber] = { difference: 0, fob: 0, kilos: 0, othersFob: 0, othersKilos: 0 };
            }

            const othersFobPerKg = othersTotalFob / othersTotalKilos;
            const diffFobPerKg = mainFobPerKg - othersFobPerKg;
            const detailDifference = diffFobPerKg * mainDetail.kilos;
            caliberData[caliber].difference += detailDifference;
            caliberData[caliber].kilos += mainDetail.kilos;
            caliberData[caliber].fob += mainDetail.fob;
            caliberData[caliber].othersKilos += othersTotalKilos;
            caliberData[caliber].othersFob += othersTotalFob;
          }
        }
      );
    });

    const calibers = Object.keys(caliberData).sort((a, b) => {
      const indexA = caliberOrder.indexOf(a);
      const indexB = caliberOrder.indexOf(b);
      return indexA - indexB;
    });

    const differenceData = calibers.map((caliber) => {
      if (displayMode === 'percentage') {
        return caliberData[caliber].fob === 0
          ? 0
          : Number(
            (
              (caliberData[caliber].difference / caliberData[caliber].fob) *
              100
            ).toFixed(2)
          );
      }
      return caliberData[caliber].difference;
    });

    const tableRows = calibers.map((caliber) => {
      const d = caliberData[caliber];
      return {
        key: caliber,
        mainKilos: d.kilos,
        othersKilos: d.othersKilos,
        totalDifference: d.difference,
        perKiloDifference: d.kilos > 0 ? d.difference / d.kilos : 0,
      };
    });

    const totalMainKilos = tableRows.reduce((acc, r) => acc + r.mainKilos, 0);
    const totalOthersKilos = tableRows.reduce((acc, r) => acc + r.othersKilos, 0);
    const totalDiff = tableRows.reduce((acc, r) => acc + r.totalDifference, 0);

    const totals = {
      mainKilos: totalMainKilos,
      othersKilos: totalOthersKilos,
      totalDifference: totalDiff,
      perKiloDifference: totalMainKilos > 0 ? totalDiff / totalMainKilos : 0,
    };

    const sortedRows = (col: SortColumn, dir: 'asc' | 'desc') => {
      return [...tableRows].sort((a, b) => {
        let aVal: number | string | undefined;
        let bVal: number | string | undefined;
        const getExternal = (key: string): number => {
          if (col === 'pctVsMedian') return pctVsMedianByKey?.[key] ?? -Infinity;
          const qd = quartileDistByKey?.[key];
          if (!qd) return -Infinity;
          if (col === 'quartile') {
            if (comparisonTarget === 'q1') return ((qd.q2Kilos + qd.q3Kilos + qd.q4Kilos) / qd.totalKilos);
            if (comparisonTarget === 'q3') return (qd.q4Kilos / qd.totalKilos);
            return ((qd.q3Kilos + qd.q4Kilos) / qd.totalKilos);
          }
          if (col === 'q1') return qd.q1Kilos / qd.totalKilos;
          if (col === 'q2') return qd.q2Kilos / qd.totalKilos;
          if (col === 'q3') return qd.q3Kilos / qd.totalKilos;
          if (col === 'q4') return qd.q4Kilos / qd.totalKilos;
          return 0;
        };
        if (['pctVsMedian', 'quartile', 'q1', 'q2', 'q3', 'q4'].includes(col)) {
          aVal = getExternal(a.key);
          bVal = getExternal(b.key);
        } else {
          aVal = a[col as keyof typeof a];
          bVal = b[col as keyof typeof b];
        }
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return dir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      });
    };

    return {
      series: [
        {
          name:
            displayMode === 'percentage'
              ? t('Diferencia (%)')
              : t('Diferencia Total'),
          data: differenceData,
        },
      ],
      calibers,
      tableRows,
      totals,
      sortedRows,
    };
  }, [data, displayMode, comparisonType, t]);

  const valueChartOptions = useMemo(
    () => ({
      ...baseChartOptions,
      dataLabels: {
        enabled: true,
        formatter: (val: number) =>
          isLocal
            ? `${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
            : `$${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`,
        style: {
          colors: [darkMode ? '#fff' : '#000'],
        },
      },
      yaxis: {
        title: {
          text: isLocal
            ? `${t('Diferencia Total')} (${currencyLabel})`
            : t('Diferencia Total (USD)'),
        },
        labels: {
          formatter: (val: number) =>
            isLocal
              ? `${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
              : `$${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`,
        },
      },
      xaxis: {
        categories: computedData.calibers,
        title: { text: t('Calibre') },
        labels: {
          rotate: -45,
          style: { fontSize: '12px' },
        },
      },
    }),
    [baseChartOptions, computedData.calibers, currencyLabel, darkMode]
  );

  const percentageChartOptions = useMemo(
    () => ({
      ...baseChartOptions,
      dataLabels: {
        enabled: true,
        formatter: (val: number) => {
          const value = Number(val);
          return isNaN(value) ? '0%' : `${Math.abs(value).toFixed(2)}%`;
        },
        style: {
          colors: [darkMode ? '#fff' : '#000'],
        },
      },
      yaxis: {
        title: { text: t('Diferencia (%)') },
        labels: {
          formatter: (val: number) => {
            const value = Number(val);
            return isNaN(value) ? '0%' : `${value.toFixed(2)}%`;
          },
        },
        tickAmount: 10,
        forceNiceScale: true,
      },
      xaxis: {
        categories: computedData.calibers,
        title: { text: t('Calibre') },
        labels: {
          rotate: -45,
          style: { fontSize: '12px' },
        },
      },
    }),
    [baseChartOptions, computedData.calibers, darkMode]
  );

  return (
    <Card className='w-full md:p-4'>
      <CardHeader>
        <div className='flex flex-col sm:flex-row justify-between w-full items-center gap-4'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold text-center sm:text-left'>
              {t('Diferencia por Calibre')}
            </h3>
            {viewMode === 'chart' && (
              <ChartDownloadButton
                chartRef={chartRef}
                title={t('Diferencia por Calibre')}
              />
            )}
          </div>
          <div className='flex gap-4 w-full sm:w-auto items-center'>
            <Tabs
              size='sm'
              selectedKey={viewMode}
              onSelectionChange={(key) =>
                setViewMode(key as 'chart' | 'table')
              }
            >
              <Tab key='chart' title={t('Gráfico')} />
              <Tab key='table' title={t('Tabla')} />
            </Tabs>
            {viewMode === 'chart' && (
              <Select
                label={t('Tipo de Valor')}
                value={displayMode}
                defaultSelectedKeys={['value']}
                onChange={(e) =>
                  setDisplayMode(e.target.value as 'value' | 'percentage')
                }
                size='sm'
                className='w-full sm:min-w-40'
              >
                <SelectItem key='value' value='value'>
                  {isLocal
                    ? `${t('Valor')} ${currencyLabel}`
                    : t('Valor USD')}
                </SelectItem>
                <SelectItem key='percentage' value='percentage'>
                  {t('Porcentaje')}
                </SelectItem>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {viewMode === 'chart' ? (
          <div ref={chartRef}>
            <ReactApexChart
              options={
                displayMode === 'value'
                  ? valueChartOptions
                  : percentageChartOptions
            }
            series={computedData.series}
            type='bar'
            height={350}
          />
          </div>
        ) : (
          <Table removeWrapper aria-label={t('Diferencia por Calibre')} sortDescriptor={{ column: sortColumn, direction: sortDirection === 'asc' ? 'ascending' : 'descending' }} onSortChange={(desc) => { setSortColumn(desc.column as SortColumn); setSortDirection(desc.direction === 'ascending' ? 'asc' : 'desc'); }}>
            <TableHeader>
              {[
                <TableColumn key='key' allowsSorting>{t('Calibre')}</TableColumn>,
                <TableColumn key='mainKilos' allowsSorting>{t('Kg Principal')}</TableColumn>,
                <TableColumn key='othersKilos' allowsSorting>{t('Kg Otros')}</TableColumn>,
                <TableColumn key='totalDifference' allowsSorting>{t('Diferencia Total')}</TableColumn>,
                <TableColumn key='perKiloDifference' allowsSorting>{t('Diferencia')} / {t('Kg Principal')}</TableColumn>,
                ...(pctVsMedianByKey ? [<TableColumn key='pctVsMedian' allowsSorting>% vs {comparisonTarget === 'median' ? 'Med.' : comparisonTarget.toUpperCase()}</TableColumn>] : []),
                ...(quartileDistByKey ? [
                  <TableColumn key='quartile' allowsSorting>KG {'>'} {comparisonTarget === 'median' ? 'Med.' : comparisonTarget.toUpperCase()}</TableColumn>,
                  <TableColumn key='q1' allowsSorting>Q1</TableColumn>,
                  <TableColumn key='q2' allowsSorting>Q2</TableColumn>,
                  <TableColumn key='q3' allowsSorting>Q3</TableColumn>,
                  <TableColumn key='q4' allowsSorting>Q4</TableColumn>,
                ] : []),
              ]}
            </TableHeader>
            <TableBody>
              {[
                <TableRow key='total' className='bg-default-50'>
                  {[
                    <TableCell key='k' className='font-bold'>{t('TOTAL')}</TableCell>,
                    <TableCell key='mk' className='font-bold'>{Math.round(computedData.totals.mainKilos).toLocaleString()}</TableCell>,
                    <TableCell key='ok' className='font-bold'>{Math.round(computedData.totals.othersKilos).toLocaleString()}</TableCell>,
                    <TableCell key='td' className={`font-bold ${computedData.totals.totalDifference > 0 ? 'text-success-500' : 'text-danger-500'}`}>{fmt(computedData.totals.totalDifference, 0)}</TableCell>,
                    <TableCell key='pk' className={`font-bold ${computedData.totals.perKiloDifference > 0 ? 'text-success-500' : 'text-danger-500'}`}>{fmt(computedData.totals.perKiloDifference)}</TableCell>,
                    ...(pctVsMedianByKey ? [<TableCell key='pctVsMedian' className='font-bold'>-</TableCell>] : []),
                    ...(quartileDistByKey ? [
                      <TableCell key='quartile' className='font-bold'>-</TableCell>,
                      <TableCell key='q1' className='font-bold'>-</TableCell>,
                      <TableCell key='q2' className='font-bold'>-</TableCell>,
                      <TableCell key='q3' className='font-bold'>-</TableCell>,
                      <TableCell key='q4' className='font-bold'>-</TableCell>,
                    ] : []),
                  ]}
                </TableRow>,
                ...computedData.sortedRows(sortColumn, sortDirection).map((row) => {
                  const score = pctVsMedianByKey?.[row.key];
                  const qd = quartileDistByKey?.[row.key];
                  const aboveMed = qd ? Math.round((comparisonTarget === 'q1' ? (qd.q2Kilos + qd.q3Kilos + qd.q4Kilos) : comparisonTarget === 'q3' ? qd.q4Kilos : (qd.q3Kilos + qd.q4Kilos)) / qd.totalKilos * 100) : null;
                  const q1Pct = qd ? Math.round((qd.q1Kilos / qd.totalKilos) * 100) : null;
                  const q2Pct = qd ? Math.round((qd.q2Kilos / qd.totalKilos) * 100) : null;
                  const q3Pct = qd ? Math.round((qd.q3Kilos / qd.totalKilos) * 100) : null;
                  const q4Pct = qd ? Math.round((qd.q4Kilos / qd.totalKilos) * 100) : null;
                  return (
                  <TableRow key={row.key}>
                    {[
                      <TableCell key='k'>{row.key}</TableCell>,
                      <TableCell key='mk'>{Math.round(row.mainKilos).toLocaleString()}</TableCell>,
                      <TableCell key='ok'>{Math.round(row.othersKilos).toLocaleString()}</TableCell>,
                      <TableCell key='td' className={row.totalDifference > 0 ? 'text-success-500' : 'text-danger-500'}>{fmt(row.totalDifference, 0)}</TableCell>,
                      <TableCell key='pk' className={row.perKiloDifference > 0 ? 'text-success-500' : 'text-danger-500'}>{fmt(row.perKiloDifference)}</TableCell>,
                      ...(pctVsMedianByKey ? [
                        <TableCell key='pctVsMedian' className={score != null ? (score >= 0 ? 'text-success-500' : 'text-danger-500') : ''}>
                          {score != null ? `${score >= 0 ? '+' : ''}${(score * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      ] : []),
                      ...(quartileDistByKey ? [
                        <TableCell key='quartile' className={aboveMed != null ? (aboveMed >= 50 ? 'text-success-500' : 'text-danger-500') : ''}>
                          {aboveMed != null ? `${aboveMed}%` : '-'}
                        </TableCell>,
                        <TableCell key='q1'>{q1Pct != null ? `${q1Pct}%` : '-'}</TableCell>,
                        <TableCell key='q2'>{q2Pct != null ? `${q2Pct}%` : '-'}</TableCell>,
                        <TableCell key='q3'>{q3Pct != null ? `${q3Pct}%` : '-'}</TableCell>,
                        <TableCell key='q4'>{q4Pct != null ? `${q4Pct}%` : '-'}</TableCell>,
                      ] : []),
                    ]}
                  </TableRow>
                  );
                }),
              ]}
            </TableBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default CaliberComparisionBarChart;
